/**
 * Privacy Settings Component
 * Allows users to control analytics tracking and marketing cookie preferences
 */

import { useState, useEffect } from 'react';
import { analyticsService } from '../../services/analytics';
import { getMarketingConsent, setMarketingConsent as setMarketingConsentValue } from '../../services/consent/consentManager';
import { enableMetaPixel } from '../../services/metaPixel/metaPixelLoader';
import { useUserProfile } from '../../hooks/useUserProfile';
import { userProfileService } from '../../services/api/UserProfileService';
import { useUserProfileContext } from '../../contexts/UserProfileContext';
import './PrivacySettings.css';

interface PrivacySettingsProps {
  isOpen: boolean;
  onClose: () => void;
}

export function PrivacySettings({ isOpen, onClose }: PrivacySettingsProps) {
  const [analyticsEnabled, setAnalyticsEnabled] = useState(true);
  const [marketingEnabled, setMarketingEnabled] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [confirmationMessage, setConfirmationMessage] = useState('');
  const [isUpdatingEmailConsent, setIsUpdatingEmailConsent] = useState(false);
  const [emailConsentError, setEmailConsentError] = useState<string | null>(null);
  const { profile } = useUserProfile();
  const { refreshProfile } = useUserProfileContext();

  // Load current opt-out status and marketing consent on modal open
  useEffect(() => {
    const optedOut = analyticsService.getOptOutStatus();
    setAnalyticsEnabled(!optedOut);
    setMarketingEnabled(getMarketingConsent());
  }, [isOpen]);

  const showConfirmationMsg = (message: string) => {
    setConfirmationMessage(message);
    setShowConfirmation(true);
    setTimeout(() => {
      setShowConfirmation(false);
    }, 3000);
  };

  const handleToggle = () => {
    const newEnabledState = !analyticsEnabled;
    const optOut = !newEnabledState;
    analyticsService.setOptOut(optOut);
    setAnalyticsEnabled(newEnabledState);
    const message = newEnabledState
      ? '✓ Analytics enabled. We\'ll collect anonymous usage data to improve the app.'
      : '✓ Analytics disabled. No usage data will be collected.';
    showConfirmationMsg(message);
  };

  const handleMarketingToggle = () => {
    const newState = !marketingEnabled;
    setMarketingConsentValue(newState);
    setMarketingEnabled(newState);

    if (newState) {
      enableMetaPixel();
      showConfirmationMsg('✓ Marketing cookies enabled. Meta Pixel has been loaded.');
    } else {
      showConfirmationMsg('✓ Marketing cookies disabled. Changes will take effect on next page load.');
    }
  };

  const handleEmailConsentToggle = async () => {
    if (!profile) return;
    
    const newConsent = !profile.emailMarketingConsent;
    setIsUpdatingEmailConsent(true);
    setEmailConsentError(null);
    
    try {
      await userProfileService.setEmailMarketingConsent(newConsent);
      await refreshProfile();
      showConfirmationMsg(
        newConsent 
          ? '✓ Email marketing enabled. You\'ll receive updates and offers.' 
          : '✓ Email marketing disabled. You won\'t receive promotional emails.'
      );
    } catch (err) {
      console.error('Failed to update email consent:', err);
      setEmailConsentError('Failed to update email preferences. Please try again.');
    } finally {
      setIsUpdatingEmailConsent(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="privacy-settings-overlay" onClick={onClose}>
      <div className="privacy-settings-modal" onClick={(e) => e.stopPropagation()}>
        <div className="privacy-settings-header">
          <h2>Privacy Settings</h2>
          <button
            className="privacy-settings-close"
            onClick={onClose}
            aria-label="Close privacy settings"
          >
            ×
          </button>
        </div>

        <div className="privacy-settings-content">
          <div className="privacy-section">
            <h3>Analytics & Usage Data</h3>
            <p className="privacy-description">
              We collect anonymous usage data to understand how you use 8-Bit Composer
              and improve the app. This helps us prioritize features and fix issues.
            </p>
            <div className="privacy-toggle-container">
              <label className="privacy-toggle-label">
                <span>Enable Analytics</span>
                <button
                  className={`privacy-toggle ${analyticsEnabled ? 'enabled' : 'disabled'}`}
                  onClick={handleToggle}
                  role="switch"
                  aria-checked={analyticsEnabled}
                  aria-label="Toggle analytics"
                >
                  <span className="privacy-toggle-slider"></span>
                </button>
              </label>
              <p className="privacy-toggle-status">
                {analyticsEnabled ? 'Analytics enabled' : 'Analytics disabled'}
              </p>
            </div>
          </div>

          <div className="privacy-section">
            <h3>Marketing & Advertising</h3>
            <p className="privacy-description">
              We use Meta Pixel to measure ad performance and show relevant ads.
              This helps us understand which marketing efforts work best.
            </p>
            <div className="privacy-toggle-container">
              <label className="privacy-toggle-label">
                <span>Enable Marketing Cookies</span>
                <button
                  className={`privacy-toggle ${marketingEnabled ? 'enabled' : 'disabled'}`}
                  onClick={handleMarketingToggle}
                  role="switch"
                  aria-checked={marketingEnabled}
                  aria-label="Toggle marketing cookies"
                >
                  <span className="privacy-toggle-slider"></span>
                </button>
              </label>
              <p className="privacy-toggle-status">
                {marketingEnabled ? 'Marketing enabled' : 'Marketing disabled'}
              </p>
            </div>
          </div>

          {profile && (
            <div className="privacy-section">
              <h3>Email Marketing</h3>
              <p className="privacy-description">
                Control whether you receive promotional emails, updates, and special offers from 8-Bit Composer.
              </p>
              <div className="privacy-toggle-container">
                <label className="privacy-toggle-label">
                  <span>Send me updates and offers</span>
                  <button
                    className={`privacy-toggle ${profile.emailMarketingConsent ? 'enabled' : 'disabled'}`}
                    onClick={handleEmailConsentToggle}
                    disabled={isUpdatingEmailConsent}
                    role="switch"
                    aria-checked={profile.emailMarketingConsent || false}
                    aria-label="Toggle email marketing"
                  >
                    <span className="privacy-toggle-slider"></span>
                  </button>
                </label>
                <p className="privacy-toggle-status">
                  {isUpdatingEmailConsent 
                    ? 'Updating...' 
                    : profile.emailMarketingConsent 
                      ? 'Email marketing enabled' 
                      : 'Email marketing disabled'}
                </p>
                {profile.emailMarketingConsentAt && (
                  <p className="privacy-toggle-status" style={{ fontSize: '0.75rem', opacity: 0.7 }}>
                    Last updated: {new Date(profile.emailMarketingConsentAt).toLocaleDateString()}
                  </p>
                )}
                {emailConsentError && (
                  <p className="privacy-toggle-status" style={{ color: 'var(--color-error, #ff4444)' }}>
                    {emailConsentError}
                  </p>
                )}
              </div>
            </div>
          )}

          {!profile && (
            <div className="privacy-section">
              <h3>Email Marketing</h3>
              <p className="privacy-description">
                Sign in to manage your email marketing preferences.
              </p>
            </div>
          )}

          {showConfirmation && (
            <div className="privacy-confirmation" role="status" aria-live="polite">
              {confirmationMessage}
            </div>
          )}

          <div className="privacy-section">
            <h3>What We Collect</h3>
            <ul className="privacy-list">
              <li>Which instruments and features you use</li>
              <li>How you interact with the sequencer grid</li>
              <li>Playback and composition patterns</li>
              <li>AI generation usage (when applicable)</li>
              <li>Session duration and engagement metrics</li>
            </ul>
          </div>

          <div className="privacy-section">
            <h3>What We Don't Collect</h3>
            <ul className="privacy-list">
              <li>IP addresses or precise location data</li>
              <li>Any data when analytics is disabled</li>
            </ul>
          </div>

          <div className="privacy-section privacy-note">
            <p>
              <strong>Note:</strong> Your browser's "Do Not Track" (DNT) setting is
              automatically respected. If DNT is enabled, analytics will be disabled
              regardless of this setting.
            </p>
          </div>
        </div>

        <div className="privacy-settings-footer">
          <button className="privacy-button-primary" onClick={onClose}>
            Done
          </button>
        </div>
      </div>
    </div>
  );
}
